import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { sha256OfFile } from "../../../lib/uploadPdf";

import { parseSchemaImport, schemaImportDifferences, type ImportedSchema } from "./schemaImport";

type Props = {
  target: {
    tenantId: string;
    templateId: string;
    templateName: string;
    versionId: string;
    versionNumber: number;
  };
  baseline: ImportedSchema;
  revision: string;
  blocked: boolean;
  dirty: boolean;
  onConfirm: (schema: ImportedSchema, expectedRevision: string) => void;
};

export function SchemaImportPanel({
  target,
  baseline,
  revision,
  blocked,
  dirty,
  onConfirm,
}: Props) {
  const { t } = useTranslation();
  const id = useId();
  const generation = useRef(0);
  const [candidate, setCandidate] = useState<{
    schema: ImportedSchema;
    hash: string;
    name: string;
  } | null>(null);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState(false);
  const [approvedHash, setApprovedHash] = useState("");
  const [approvedRevision, setApprovedRevision] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  useEffect(
    () => () => {
      generation.current++;
    },
    [],
  );
  const differences = candidate ? schemaImportDifferences(baseline, candidate.schema) : [];
  const matching =
    candidate &&
    /^[a-f0-9]{64}$/.test(approvedHash) &&
    approvedHash === candidate.hash &&
    approvedRevision === revision;
  const canApply =
    !blocked && !dirty && !reading && matching && confirmed && differences.length > 0;
  const inputClass =
    "mt-1 block w-full rounded border border-slate-300 p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:bg-slate-100";

  async function pick(file: File | undefined) {
    const run = ++generation.current;
    setCandidate(null);
    setConfirmed(false);
    setApprovedHash("");
    setApprovedRevision("");
    setError(false);
    setReading(!!file);
    if (!file) return;
    try {
      if (file.size > 1024 * 1024) throw new Error("size_limit");
      const [text, hash] = await Promise.all([file.text(), sha256OfFile(file)]);
      const schema = parseSchemaImport(text);
      if (run === generation.current) setCandidate({ schema, hash, name: file.name });
    } catch {
      if (run === generation.current) setError(true);
    } finally {
      if (run === generation.current) setReading(false);
    }
  }

  return (
    <details className="mt-4 rounded-xl border border-slate-200 bg-white p-4 text-sm">
      <summary className="focus-visible:ring-brand-500 cursor-pointer rounded font-medium focus-visible:outline-none focus-visible:ring-2">
        {t("form_templates.import.title")}
      </summary>
      <p className="mt-3 text-slate-600">{t("form_templates.import.intro")}</p>
      <dl className="mt-3 space-y-2 break-all">
        <div>
          <dt className="font-medium">{t("form_templates.import.tenant")}</dt>
          <dd>{target.tenantId}</dd>
        </div>
        <div>
          <dt className="font-medium">{t("form_templates.import.template")}</dt>
          <dd>
            {target.templateName} — {target.templateId}
          </dd>
        </div>
        <div>
          <dt className="font-medium">{t("form_templates.import.version")}</dt>
          <dd>
            v{target.versionNumber} — {target.versionId}
          </dd>
        </div>
      </dl>
      {dirty && (
        <p role="status" className="mt-3 text-amber-800">
          {t("form_templates.import.dirty")}
        </p>
      )}
      {blocked && (
        <p role="status" className="mt-3 text-amber-800">
          {t("form_templates.import.blocked")}
        </p>
      )}
      <label className="mt-3 block" htmlFor={`${id}-file`}>
        {t("form_templates.import.file")}
      </label>
      <input
        id={`${id}-file`}
        type="file"
        accept="application/json,.json"
        className={`${inputClass} min-w-0`}
        disabled={blocked}
        onChange={(e) => void pick(e.target.files?.[0])}
      />
      {reading && <p role="status">{t("form_templates.import.reading")}</p>}
      {error && (
        <p role="alert" className="mt-3 text-red-700">
          {t("form_templates.import.invalid")}
        </p>
      )}
      {candidate && (
        <div className="mt-4 space-y-3">
          <p className="break-all">{candidate.name}</p>
          <div>
            <label htmlFor={`${id}-hash`}>{t("form_templates.import.hash")}</label>
            <textarea
              id={`${id}-hash`}
              readOnly
              rows={3}
              value={candidate.hash}
              className={`${inputClass} font-mono text-xs`}
            />
          </div>
          <p>
            {t("form_templates.import.counts", {
              sections: candidate.schema.sections.length,
              fields: candidate.schema.sections.reduce((n, s) => n + s.fields.length, 0),
              differences: differences.length,
            })}
          </p>
          <div
            className="max-h-96 space-y-2 overflow-y-auto rounded border border-slate-200 p-2"
            aria-label={t("form_templates.import.differences")}
          >
            {differences.map((d) => (
              <details key={d.path} className="rounded border border-slate-200 p-2">
                <summary className="focus-visible:ring-brand-500 cursor-pointer break-all focus-visible:outline-none focus-visible:ring-2">
                  {t(`form_templates.import.${d.kind}`)} — {d.path}
                </summary>
                <p className="mt-2 font-medium">{t("form_templates.import.before")}</p>
                <pre className="whitespace-pre-wrap break-all text-xs">
                  {JSON.stringify(d.before ?? null, null, 2)}
                </pre>
                <p className="mt-2 font-medium">{t("form_templates.import.after")}</p>
                <pre className="whitespace-pre-wrap break-all text-xs">
                  {JSON.stringify(d.after ?? null, null, 2)}
                </pre>
              </details>
            ))}
          </div>
          <div>
            <label htmlFor={`${id}-approved-hash`}>
              {t("form_templates.import.approved_hash")}
            </label>
            <input
              id={`${id}-approved-hash`}
              className={`${inputClass} font-mono text-xs`}
              value={approvedHash}
              spellCheck={false}
              autoComplete="off"
              disabled={blocked}
              onChange={(e) => {
                setApprovedHash(e.target.value.trim());
                setConfirmed(false);
              }}
            />
          </div>
          <div>
            <label htmlFor={`${id}-approved-revision`}>
              {t("form_templates.import.approved_revision")}
            </label>
            <input
              id={`${id}-approved-revision`}
              className={`${inputClass} font-mono text-xs`}
              value={approvedRevision}
              spellCheck={false}
              autoComplete="off"
              disabled={blocked}
              onChange={(e) => {
                setApprovedRevision(e.target.value.trim());
                setConfirmed(false);
              }}
            />
          </div>
          {!matching && (
            <p role="status" className="text-amber-800">
              {t("form_templates.import.mismatch")}
            </p>
          )}
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-1"
              checked={confirmed}
              disabled={blocked || dirty || !matching}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            <span>{t("form_templates.import.confirm")}</span>
          </label>
          <button
            type="button"
            disabled={!canApply}
            className="bg-brand-600 focus-visible:ring-brand-500 rounded-lg px-4 py-2 font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:bg-slate-300"
            onClick={() => {
              if (canApply && candidate) {
                setConfirmed(false);
                onConfirm(candidate.schema, approvedRevision);
              }
            }}
          >
            {t("form_templates.import.apply")}
          </button>
        </div>
      )}
    </details>
  );
}
